/**
 * Volume do som que vem da tela transmitida.
 *
 * É separado do volume da voz de quem transmite, e essa é a razão de existir:
 * um jogo, um vídeo ou uma reunião gravada costumam chegar bem mais altos do
 * que a pessoa falando por cima, e antes só dava para abaixar os dois juntos,
 * emudecendo justamente quem estava explicando o que aparecia na tela.
 *
 * Como todo ajuste de volume por aqui, vale só para quem mexeu: não altera o
 * que os outros ouvem nem avisa quem está transmitindo.
 */

import { EyeOff, MonitorSpeaker, Volume2, VolumeX } from 'lucide-react'
import { ContextMenu, MenuItem, MenuSeparator, Slider } from '@/components/ui'
import { usePreferencesStore } from '@/store/preferences'

export interface PosicaoDoMenu {
  x: number
  y: number
}

export function ScreenAudioMenu({
  userId,
  nome,
  posicao,
  /** Falso quando a transmissão não carrega áudio nenhum. */
  temAudio,
  onFechar,
  onClose,
}: {
  userId: string
  nome: string
  posicao: PosicaoDoMenu
  temAudio: boolean
  /** Deixar de assistir. Ausente na própria tela. */
  onFechar?: () => void
  onClose: () => void
}) {
  const volume = usePreferencesStore((s) => s.volumesDeTela[userId] ?? 100)
  const setVolumeDeTela = usePreferencesStore((s) => s.setVolumeDeTela)

  const silenciado = volume === 0

  return (
    <ContextMenu x={posicao.x} y={posicao.y} onClose={onClose}>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <MonitorSpeaker className="size-4 shrink-0 text-content-muted" />
        <p className="truncate text-[13px] font-semibold text-content-primary">Tela de {nome}</p>
      </div>

      <MenuSeparator />

      {temAudio ? (
        <div className="px-2 pt-1 pb-2">
          <Slider
            value={volume}
            onChange={(v) => setVolumeDeTela(userId, v)}
            min={0}
            max={200}
            step={5}
            label="Volume da transmissão"
            formatValue={(v) => `${v}%`}
          />

          <div className="mt-1 flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setVolumeDeTela(userId, silenciado ? 100 : 0)}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-surface-raised px-2 py-1.5 text-xs text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
            >
              {silenciado ? <Volume2 className="size-3.5" /> : <VolumeX className="size-3.5" />}
              {silenciado ? 'Ouvir a tela' : 'Silenciar a tela'}
            </button>

            <button
              type="button"
              onClick={() => setVolumeDeTela(userId, 100)}
              disabled={volume === 100}
              className="rounded-md bg-surface-raised px-2 py-1.5 text-xs text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary disabled:opacity-40"
            >
              Padrão
            </button>
          </div>
        </div>
      ) : (
        /* Dizer que não há som é melhor do que oferecer um controle que não
           faria diferença nenhuma. */
        <p className="px-2.5 py-2 text-xs leading-relaxed text-content-muted">
          Esta transmissão está sendo enviada sem som.
        </p>
      )}

      {onFechar ? (
        <>
          <MenuSeparator />
          <MenuItem icon={<EyeOff />} onClick={onFechar}>
            Não assistir
          </MenuItem>
        </>
      ) : null}
    </ContextMenu>
  )
}
